import React, { useState, useMemo, useCallback } from 'react';
import { Save, Lock, Check, Search, CheckCheck, Square, Asterisk, ShieldAlert, Database } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { saveFieldConfig, setMaterialSettings } from './materialApi';
import ConfigPresetsBar from './ConfigPresetsBar';

/**
 * MaterialFieldConfig — Admin-Maske: pro Katalog-Feld aktiv/Pflicht schalten.
 * Pflichtkern (core) ist gesperrt (immer aktiv + Pflicht). „required" impliziert
 * „enabled". Nur Nicht-Kern-Felder werden gespeichert.
 *
 * Bulk: global + sektionsweise „alle aktiv / alle Pflicht / alle ab"; Suchfilter.
 *
 * Props: user, config {views, fields}, onSaved(newConfig), showSuccess, showError
 */
const MaterialFieldConfig = ({ user, config, onSaved, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t, language } = useLanguage();
  const lang = language === 'en' ? 'en' : 'de';

  const [fields, setFields] = useState(() => (config?.fields || []).map((f) => ({ ...f })));
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');

  const activeCount = useMemo(() => fields.filter((f) => f.enabled).length, [fields]);

  // --- Mutationen (core bleibt immer unangetastet) ---
  const applyPatch = useCallback((predicate, patch) => {
    setFields((prev) =>
      prev.map((f) => {
        if (f.core || !predicate(f)) return f;
        const next = { ...f, ...patch };
        if (!next.enabled) next.required = false; // required impliziert enabled
        return next;
      })
    );
  }, []);

  const update = useCallback((key, patch) => applyPatch((f) => f.key === key, patch), [applyPatch]);
  const setSectionAll = useCallback((view, patch) => applyPatch((f) => f.view === view, patch), [applyPatch]);
  const setGlobalAll = useCallback((patch) => applyPatch(() => true, patch), [applyPatch]);

  // Preset lokal anwenden ([{field_key, enabled, required, sap_visible}]); core bleibt.
  const applyConfig = useCallback((cfgArr) => {
    const map = Object.fromEntries((cfgArr || []).map((c) => [c.field_key, c]));
    setFields((prev) => prev.map((f) => {
      if (f.core) return f;
      const c = map[f.key];
      const enabled = !!(c && c.enabled);
      return { ...f, enabled, required: enabled && !!c.required, sapVisible: !!(c && c.sap_visible) };
    }));
  }, []);

  // Auf Katalog-Standard zuruecksetzen (defEnabled/defRequired/defSapVisible aus field-config).
  const resetDefaults = useCallback(() => {
    setFields((prev) => prev.map((f) => (f.core ? f : { ...f, enabled: !!f.defEnabled, required: !!f.defRequired, sapVisible: !!f.defSapVisible })));
  }, []);

  // Dublettenpruefung-Toggle (Modul-Einstellung)
  const [dupcheck, setDupcheck] = useState(!!config?.settings?.dupcheckEnabled);
  const toggleDupcheck = async (v) => {
    setDupcheck(v);
    try {
      const r = await setMaterialSettings(user, { dupcheckEnabled: v });
      // Geteilten config-State des Parents aktualisieren, damit die Erfassungsmaske
      // die neue Einstellung sofort sieht (nicht erst nach Reload).
      onSaved?.({ ...config, settings: r?.settings || { ...(config?.settings || {}), dupcheckEnabled: v } });
      showSuccess(t('material.settingsSaved'));
    } catch { setDupcheck(!v); showError(t('material.configSaveError')); }
  };

  // --- Suchfilter ---
  const matches = useCallback(
    (f) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return (
        f.labelDe.toLowerCase().includes(q) ||
        f.labelEn.toLowerCase().includes(q) ||
        f.sap.toLowerCase().includes(q) ||
        f.key.includes(q)
      );
    },
    [query]
  );

  const sections = useMemo(() => {
    const byView = {};
    for (const f of fields) if (matches(f)) (byView[f.view] ||= []).push(f);
    return (config?.views || [])
      .map((v) => ({ ...v, fields: byView[v.key] || [] }))
      .filter((v) => v.fields.length > 0);
  }, [fields, config, matches]);

  const save = async () => {
    setSaving(true);
    try {
      const items = fields
        .filter((f) => !f.core)
        .map((f) => ({ field_key: f.key, enabled: !!f.enabled, required: !!f.required, sap_visible: !!f.sapVisible }));
      const fresh = await saveFieldConfig(user, items);
      showSuccess(t('material.configSaved'));
      onSaved?.(fresh);
    } catch (e) {
      showError(t('material.configSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const cardCls = isDarkMode
    ? 'bg-gray-900/30 border border-white/10 backdrop-blur-xl'
    : 'bg-white border border-slate-200';
  const inputBase = isDarkMode
    ? 'bg-white/5 border-white/15 text-white placeholder-white/30'
    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400';

  // Mini-Button fuer Bulk-Aktionen
  const Bulk = ({ onClick, Icon, label, tone }) => (
    <button type="button" onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium transition-colors ${
        tone === 'off'
          ? (isDarkMode ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100')
          : (isDarkMode ? 'text-emerald-300/80 hover:text-emerald-200 hover:bg-emerald-500/10' : 'text-emerald-600 hover:bg-emerald-50')
      }`}>
      <Icon className="w-3 h-3" /> {label}
    </button>
  );

  const Toggle = ({ checked, disabled, onChange }) => (
    <button type="button" disabled={disabled} onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${
        checked ? 'bg-emerald-500' : isDarkMode ? 'bg-white/15' : 'bg-slate-300'
      }`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Kopfzeile: Zaehler + globale Bulk + Speichern */}
      <div className={`rounded-2xl p-4 ${cardCls}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{t('material.configTitle')}</div>
            <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
              {t('material.activeFields', { n: activeCount, total: fields.length })}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Bulk onClick={() => setGlobalAll({ enabled: true })} Icon={CheckCheck} label={t('material.allActive')} />
            <Bulk onClick={() => setGlobalAll({ enabled: true, required: true })} Icon={Asterisk} label={t('material.allRequired')} />
            <Bulk onClick={() => setGlobalAll({ sapVisible: true })} Icon={Database} label={t('material.allSap')} />
            <Bulk onClick={() => setGlobalAll({ enabled: false, sapVisible: false })} Icon={Square} label={t('material.allOff')} tone="off" />
            <button type="button" disabled={saving} onClick={save}
              className="ml-1 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-all disabled:opacity-50 shadow-lg shadow-emerald-600/20">
              <Save className="w-4 h-4" /> {t('material.configSave')}
            </button>
          </div>
        </div>
        {/* Presets + Standard zuruecksetzen */}
        <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <ConfigPresetsBar user={user} fields={fields} onApplyConfig={applyConfig} onResetDefaults={resetDefaults}
            showSuccess={showSuccess} showError={showError} />
        </div>
        {/* Optionale Features */}
        <div className={`mt-3 pt-3 border-t flex items-center justify-between gap-3 ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <div className="min-w-0">
            <div className={`text-sm flex items-center gap-2 ${isDarkMode ? 'text-white/90' : 'text-slate-700'}`}>
              <ShieldAlert className={`w-4 h-4 ${isDarkMode ? 'text-amber-300' : 'text-amber-500'}`} /> {t('material.dupSetting')}
            </div>
            <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{t('material.dupSettingHint')}</div>
          </div>
          <Toggle checked={dupcheck} onChange={toggleDupcheck} />
        </div>
        {/* Suche */}
        <div className="relative mt-3">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('material.configSearch')}
            className={`w-full rounded-lg border pl-9 pr-3 py-2 text-sm outline-none ${inputBase}`} />
        </div>
      </div>

      {sections.map((sec) => (
        <div key={sec.key} className={`rounded-2xl overflow-hidden ${cardCls}`}>
          <div className={`px-4 py-2.5 flex items-center justify-between gap-3 ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
            <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
              {lang === 'en' ? sec.en : sec.de}
              <span className={`ml-2 text-xs font-normal ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                {sec.fields.filter((f) => f.enabled).length}/{sec.fields.length}
              </span>
            </span>
            <div className="flex items-center gap-1">
              <Bulk onClick={() => setSectionAll(sec.key, { enabled: true })} Icon={CheckCheck} label={t('material.allActive')} />
              <Bulk onClick={() => setSectionAll(sec.key, { enabled: true, required: true })} Icon={Asterisk} label={t('material.allRequired')} />
              <Bulk onClick={() => setSectionAll(sec.key, { sapVisible: true })} Icon={Database} label={t('material.allSap')} />
              <Bulk onClick={() => setSectionAll(sec.key, { enabled: false, sapVisible: false })} Icon={Square} label={t('material.allOff')} tone="off" />
            </div>
          </div>
          <div className={isDarkMode ? 'divide-y divide-white/5' : 'divide-y divide-slate-100'}>
            {sec.fields.map((f) => (
              <div key={f.key} className="px-4 py-2.5 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className={`text-sm flex items-center gap-2 ${isDarkMode ? 'text-white/90' : 'text-slate-700'}`}>
                    {lang === 'en' ? f.labelEn : f.labelDe}
                    {f.core && <Lock className={`w-3 h-3 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`} />}
                  </div>
                  <div className={`font-mono text-[10px] ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>{f.sap}</div>
                </div>
                <div className="flex items-center gap-5 flex-shrink-0">
                  {f.core ? (
                    <span className={`inline-flex items-center gap-1 text-xs ${isDarkMode ? 'text-emerald-300/80' : 'text-emerald-600'}`}>
                      <Check className="w-3.5 h-3.5" /> {t('material.coreField')}
                    </span>
                  ) : (
                    <>
                      <div className="flex flex-col items-center gap-0.5">
                        <Toggle checked={f.enabled} onChange={(v) => update(f.key, { enabled: v })} />
                        <span className={`text-[10px] ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('material.toggleActive')}</span>
                      </div>
                      <div className="flex flex-col items-center gap-0.5">
                        <Toggle checked={f.required} disabled={!f.enabled} onChange={(v) => update(f.key, { required: v })} />
                        <span className={`text-[10px] ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('material.toggleRequired')}</span>
                      </div>
                      <div className="flex flex-col items-center gap-0.5">
                        <Toggle checked={f.sapVisible} onChange={(v) => update(f.key, { sapVisible: v })} />
                        <span className={`text-[10px] ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('material.toggleSap')}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MaterialFieldConfig;
